import { FC, memo, useMemo } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Flex } from 'antd';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import translations from './RoleAccessGroupTable.translations.json';
import {
  TanstackFormTable,
  FormField,
  CreateFormTableRowButton,
  DeleteFormTableRowButton,
  AccessGroupDto,
  useInfiniteSearchAccessGroupsQuery,
  createInfiniteSearchSelect,
  getFormTableFieldPath,
  TextEllipsis,
  AccessGroupOptionDto,
} from '@atlas-frontend/sdk';
import { useFormContext, useWatch } from 'react-hook-form';
import { UiLink } from '../../paths/UiLink';
import { path } from '../../paths';

const { addTranslations, useTranslation } = i18nNsFactory(
  'role-access-group-table'
);
addTranslations(translations);

const columnHelper = createColumnHelper<AccessGroupDto>();

export const RoleAccessGroupFormTable = () => {
  const { t } = useTranslation();

  const columns = useMemo(() => {
    const columns: ColumnDef<AccessGroupDto, any>[] = [
      columnHelper.accessor('name', {
        header: t('column.name'),
        cell: AccessGroupCell,
      }),
      columnHelper.display({
        id: 'operations',
        header: () => (
          <Flex justify="center" gap="small">
            <CreateFormTableRowButton autoFocusField="name" />
          </Flex>
        ),
        cell: (props) => (
          <Flex justify="center" gap="small">
            <DeleteFormTableRowButton context={props} />
          </Flex>
        ),
        enableSorting: false,
        enableColumnFilter: false,
        meta: {
          width: 88,
          className: 'sticky-right',
        },
      }),
    ];

    return columns;
  }, [t]);

  return (
    <Flex align="end" gap="middle" vertical>
      <TanstackFormTable
        name="accessGroups"
        columns={columns}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
};

const AccessGroupInfiniteSelect = createInfiniteSearchSelect(
  useInfiniteSearchAccessGroupsQuery
);

const AccessGroupCell: FC<CellContext<AccessGroupDto, any>> = memo((props) => {
  const { setValue } = useFormContext();
  const isEditable = !props.row.original.id;

  const basePath = getFormTableFieldPath(props).slice(0, -1);
  const id = [...basePath, 'id'].join('.');
  const name = [...basePath, 'name'].join('.');
  const idValue = useWatch({ name: id });

  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (value) =>
          !isEditable ? (
            <TextEllipsis>
              <UiLink
                id={`role-access-group-${props.row.index}`}
                path={path.accessGroupDetails}
                params={{ id: idValue }}
                target="_blank"
              >
                {value}
              </UiLink>
            </TextEllipsis>
          ) : (
            value
          ),
        previewOnly: !isEditable,
      }}
    >
      <AccessGroupInfiniteSelect
        size="small"
        name={name}
        autoFocus
        defaultOpen
        onChange={(_value, option: AccessGroupOptionDto) => {
          setValue(id, option.item.id);
        }}
      />
    </FormField>
  );
});
